import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Building2, Mail, Phone, Users } from 'lucide-react';
import { portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatMoney, formatMonth, formatPercent, hasPermission } from '@blimo/shared';
import { CostsView, MonthRangePicker, OpenItemsView } from '@blimo/ui/finance';
import { Badge, Button, DescriptionList, EmptyState, ErrorState, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, initials } from '@blimo/ui';
import { CardsSkeleton, ContactCard, ContactDetails, ManagerCard, MyRoleBadges, UnitList, UnitRow } from '../components/property-bits.jsx';
import { PageHeader } from '../components/ui/page-header.jsx';
import { ItemIcon, ItemList, ItemRow, Section } from '../components/ui/section.jsx';
import { AccumulatedSection } from './finance.jsx';
import { InvestmentBaselineSettings } from './baseline-form.jsx';

/** Meine Immobilien – one card per visible property. */
export function PropertiesPage() {
  const api = useApi();
  const { data: properties, error, isPending, refetch } = useQuery(portalQueries.properties(api));
  const { data: me } = useMe();
  // G-19 / F-125: SE-Kennzahlen je Liegenschaft für Mandanten (finance.view) – berechnet im Backend
  const figuresQuery = useQuery({ ...portalQueries.financeProperties(api), enabled: hasPermission(me, 'finance.view') });
  const figures = new Map((figuresQuery.data ?? []).map((f) => [f.property_id, f]));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Meine Immobilien"
        compactTitle="Immobilien"
        description="Liegenschaften, in denen Ihnen Einheiten gehören oder für die Sie zuständig sind."
      />
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <CardsSkeleton />
      ) : properties.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Noch keine Immobilien zugeordnet"
          description="Ihrem Konto sind derzeit keine Liegenschaften zugeordnet. Bitte wenden Sie sich an Ihre Verwaltung, wenn hier Einheiten fehlen."
        />
      ) : (
        <ul className="grid items-start gap-4 lg:grid-cols-2" aria-label="Meine Immobilien">
          {properties.map((property) => (
            <li key={property.id}>
              <PropertyCard property={property} figures={figures.get(property.id)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PropertyCard({ property, figures }) {
  const api = useApi();
  const units = property.my_units ?? [];
  const name = property.name || property.address?.line;
  const addressId = useId();
  return (
    <article data-slot="section" className="flex flex-col overflow-hidden rounded-xl border bg-card" aria-label={name}>
      {property.has_image && (
        <img
          src={api.portal.properties.imageUrl(property.id, property.image_version)}
          alt=""
          className="aspect-[16/6] w-full border-b object-cover"
          loading="lazy"
        />
      )}
      <ItemRow
        as={Link}
        to="/immobilien/$propertyId"
        params={{ propertyId: String(property.id) }}
        aria-label={name}
        aria-describedby={addressId}
        leading={<ItemIcon icon={Building2} className="size-9" />}
        title={<span className="text-[0.9375rem] font-semibold">{name}</span>}
        subtitle={
          <span className="flex flex-col gap-1.5">
            <span id={addressId}>{property.address?.line}</span>
            <MyRoleBadges roles={property.my_roles} />
          </span>
        }
        className="border-b bg-muted/40 py-3 hover:bg-muted/70"
      />
      {figures && <PropertyFigures figures={figures} />}
      <section aria-label={`Meine Einheiten in ${property.name}`} className="flex flex-col">
        <h3 className="px-4 pb-1 pt-3 text-xs font-medium text-muted-foreground">
          Meine Einheiten <span className="tabular-nums">({units.length})</span>
        </h3>
        {units.length === 0 ? (
          <p className="px-4 pb-3 text-[0.8125rem] text-muted-foreground">Keine eigenen Einheiten – Sie sind hier in einer Objektrolle tätig.</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {units.map((unit) => (
              <li key={unit.id}>
                <UnitRow unit={unit} />
              </li>
            ))}
          </ul>
        )}
      </section>
      {property.contact && (
        <section aria-label="Ansprechpartner" className="flex flex-col gap-2 border-t px-4 py-3">
          <h3 className="text-xs font-medium text-muted-foreground">Ansprechpartner</h3>
          <ContactDetails contact={property.contact} compact />
        </section>
      )}
    </article>
  );
}

const TABS = ['uebersicht', 'einheiten', 'eigentuemer', 'entwicklung', 'offene-posten', 'kosten', 'einstellungen'];

/** Liegenschaft – address, contact, my units and (for owners) the owner list. */
export function PropertyDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { propertyId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const { data: property, error, isPending, refetch } = useQuery(portalQueries.property(api, propertyId));
  // 404 = not an owner here → the tab stays hidden (the API decides, not the frontend).
  const owners = useQuery({ ...portalQueries.propertyOwners(api, propertyId), enabled: Boolean(property) });
  const showOwners = owners.isSuccess;
  // Finance tabs (investor): finance.view; baseline settings additionally finance.baselines.manage.
  const showFinance = hasPermission(me, 'finance.view');
  const showSettings = showFinance && hasPermission(me, 'finance.baselines.manage');
  const available = {
    uebersicht: true,
    einheiten: true,
    eigentuemer: showOwners,
    entwicklung: showFinance,
    'offene-posten': showFinance,
    kosten: showFinance,
    einstellungen: showSettings,
  };
  const requested = TABS.includes(search.tab) ? search.tab : 'uebersicht';
  const tab = available[requested] ? requested : 'uebersicht';
  const setTab = (next) =>
    navigate({ to: '/immobilien/$propertyId', params: { propertyId }, search: { tab: next === 'uebersicht' ? undefined : next }, replace: true });

  const back = { to: '/immobilien', label: 'Immobilien' };

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Liegenschaft" back={back} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Liegenschaft nicht gefunden"
          notFoundDescription="Diese Liegenschaft gibt es nicht oder sie ist Ihrem Konto nicht zugeordnet."
          action={
            <Button asChild variant="outline">
              <Link to="/immobilien">Zu meinen Immobilien</Link>
            </Button>
          }
        />
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label="Wird geladen">
        <PageHeader title="Liegenschaft" back={back} />
        <CardsSkeleton />
      </div>
    );
  }

  const address = property.address ?? {};
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        back={back}
        title={property.name || address.line}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {address.line}
            <MyRoleBadges roles={property.my_roles} />
          </span>
        }
      />
      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <TabsList aria-label="Bereiche der Liegenschaft">
          <TabsTrigger value="uebersicht">Übersicht</TabsTrigger>
          <TabsTrigger value="einheiten">Meine Einheiten</TabsTrigger>
          {showOwners && <TabsTrigger value="eigentuemer">Eigentümer</TabsTrigger>}
          {showFinance && <TabsTrigger value="entwicklung">Entwicklung</TabsTrigger>}
          {showFinance && <TabsTrigger value="offene-posten">Offene Posten</TabsTrigger>}
          {showFinance && <TabsTrigger value="kosten">Kosten</TabsTrigger>}
          {showSettings && <TabsTrigger value="einstellungen">Einstellungen</TabsTrigger>}
        </TabsList>
        <TabsContent value="uebersicht" className="grid items-start gap-4 md:grid-cols-2">
          <Section title="Adresse">
            <DescriptionList
              labelWidth="8rem"
              items={[
                { label: 'Straße', value: [address.street, address.house_number].filter(Boolean).join(' ') },
                { label: 'Ort', value: [address.postal_code, address.city].filter(Boolean).join(' ') },
                { label: 'Meine Einheiten', value: <span className="numeric">{String(property.my_units?.length ?? 0)}</span> },
              ]}
            />
          </Section>
          <ContactCard contact={property.contact} title="Ansprechpartner" />
          {property.manager && <ManagerCard manager={property.manager} />}
          {property.has_image && (
            <Section title="Bild">
              <img
                src={api.portal.properties.imageUrl(property.id, property.image_version)}
                alt={`Bild ${property.name ?? ''}`.trim()}
                className="max-h-72 w-full rounded-lg object-cover"
                loading="lazy"
              />
            </Section>
          )}
        </TabsContent>
        <TabsContent value="einheiten">
          <UnitList units={property.my_units} label="Meine Einheiten" />
        </TabsContent>
        {showOwners && (
          <TabsContent value="eigentuemer">
            <OwnerList owners={owners.data} />
          </TabsContent>
        )}
        {showFinance && (
          <TabsContent value="entwicklung" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold">Kumulierte Entwicklung seit Kauf</h2>
              <Link to="/entwicklung" search={{ property: String(property.id) }} className="text-[0.8125rem] font-medium text-primary hover:underline">
                Monatliche Mietentwicklung
              </Link>
            </div>
            <AccumulatedSection propertyId={String(property.id)} canManage={showSettings} />
          </TabsContent>
        )}
        {showFinance && (
          <TabsContent value="offene-posten">
            <PropertyOpenItems propertyId={String(property.id)} />
          </TabsContent>
        )}
        {showFinance && (
          <TabsContent value="kosten">
            <PropertyCosts propertyId={String(property.id)} />
          </TabsContent>
        )}
        {showSettings && (
          <TabsContent value="einstellungen" className="flex flex-col gap-3">
            <div>
              <h2 className="text-base font-semibold">Investitions-Startwerte</h2>
              <p className="text-[0.8125rem] text-muted-foreground">Grundlage für Rendite, Veränderung und kumulierte Entwicklung dieser Liegenschaft.</p>
            </div>
            <InvestmentBaselineSettings propertyId={String(property.id)} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/** Open items of one property (GET /portal/finance/open-items?property=). */
function PropertyOpenItems({ propertyId }) {
  const api = useApi();
  const query = useQuery(portalQueries.financeOpenItems(api, { property: propertyId }));
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) return <Skeleton className="h-40 w-full" />;
  return <OpenItemsView data={query.data} showPropertyHeading={false} emptyDescription="Für diese Liegenschaft sind derzeit keine offenen Posten gebucht." />;
}

/** Owners of the WEG. Contact data only if the owner consented (API returns null otherwise). */
function OwnerList({ owners }) {
  if (!owners?.length) return <EmptyState compact icon={Users} title="Keine Eigentümer gefunden" />;
  return (
    <Section
      title="Eigentümer"
      description={`${owners.length} ${owners.length === 1 ? 'Eintrag' : 'Einträge'}`}
      footer="Kontaktdaten werden nur angezeigt, wenn die jeweiligen Eigentümer der Weitergabe an Miteigentümer zugestimmt haben."
      flush
    >
      <ItemList aria-label="Eigentümerliste">
        {owners.map((owner) => (
          <li key={`${owner.party_id}-${owner.unit}`} className="flex flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="portal-avatar size-8 text-[0.6875rem]" aria-hidden="true">
                {initials(owner.name)}
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                  {owner.name}
                  {owner.is_me && <Badge variant="secondary">Sie</Badge>}
                </span>
                {owner.unit && <span className="text-[0.8125rem] text-muted-foreground">{owner.unit}</span>}
              </div>
            </div>
            {owner.contact && (owner.contact.email || owner.contact.phone) ? (
              <div className="flex flex-col gap-0.5 pl-11 text-[0.8125rem] sm:items-end sm:pl-0">
                {owner.contact.email && (
                  <a href={`mailto:${owner.contact.email}`} className="inline-flex min-h-7 items-center gap-1.5 break-all hover:text-(--primary-text)">
                    <Mail className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    {owner.contact.email}
                  </a>
                )}
                {owner.contact.phone && (
                  <a
                    href={`tel:${owner.contact.phone.replace(/[^\d+]/g, '')}`}
                    className="inline-flex min-h-7 items-center gap-1.5 tabular-nums hover:text-(--primary-text)"
                  >
                    <Phone className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    {owner.contact.phone}
                  </a>
                )}
              </div>
            ) : (
              <span className="pl-11 text-xs text-muted-foreground sm:pl-0">Kontaktdaten nicht freigegeben</span>
            )}
          </li>
        ))}
      </ItemList>
    </Section>
  );
}

/**
 * SE-Liegenschaftsliste (F-125): Einheiten, OP-Saldo, Rendite, Kosten der letzten 6 Monate – Werte aus
 * GET /portal/finance/properties (nur Anzeige).
 * @param {{ figures: { units: number, open_items_balance: number, yield: number|null, yield_period: string|null, costs_last_6_months: number|null } }} props
 */
function PropertyFigures({ figures }) {
  const items = [
    { label: 'Einheiten', value: String(figures.units) },
    {
      label: 'Offene Posten',
      value: figures.open_items_balance === 0 ? '–' : formatMoney(figures.open_items_balance),
      tone: figures.open_items_balance < 0 ? 'text-destructive' : undefined,
    },
    {
      label: figures.yield_period ? `Rendite ${formatMonth(figures.yield_period)}` : 'Rendite',
      value: figures.yield == null ? '–' : formatPercent(figures.yield),
    },
    { label: 'Kosten 6 Monate', value: figures.costs_last_6_months == null ? '–' : formatMoney(figures.costs_last_6_months) },
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-b px-4 py-3 sm:grid-cols-4" aria-label="Kennzahlen">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col">
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd className={`numeric text-sm font-medium ${item.tone ?? ''}`}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** "YYYY-MM" of today minus n months (period presets only – amounts are computed by the API). @param {number} n */
function monthKey(n) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Kosten-Tab (F-126): Kostenbuchungen der freigegebenen Konten dieser Liegenschaft (GET /portal/finance/costs?property=). */
function PropertyCosts({ propertyId }) {
  const api = useApi();
  const [period, setPeriod] = useState({ from: monthKey(5), to: monthKey(0) });
  const query = useQuery(portalQueries.financeCosts(api, { ...period, property: propertyId }));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <MonthRangePicker from={period.from} to={period.to} onChange={(range) => setPeriod((prev) => ({ ...prev, ...range }))} size="sm" />
        <Link to="/kosten" search={{ property: propertyId }} className="text-[0.8125rem] font-medium text-primary hover:underline">
          Alle Kosten
        </Link>
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <CostsView
          data={query.data}
          showPropertyHeading={false}
          emptyDescription="Für diese Liegenschaft sind im Zeitraum keine Kosten freigegebener Konten gebucht."
        />
      )}
    </div>
  );
}
